'use client';
import React, { useEffect, useState } from 'react';
import { Icon } from '@/src/components/ui';
import { IUIProfile } from '@/src/types/IUIProfile';
import { Button } from '@workspace/ui/components/button';
import { EmojiPicker } from '@/src/components/ui/EmojiPicker';
import { cn } from '@workspace/ui/lib/utils';
import type { Editor } from '@tiptap/react';

export type FeatureType = 'video' | 'image' | 'gif' | 'youtube' | 'connect';

interface Feature {
  id: FeatureType;
  icon: string;
  cost: number;
  enabled: boolean;
}

interface Props {
  onFeaturesChange?: (selected: FeatureType[]) => void;
  profile?: IUIProfile;
  editor?: Editor;
  onGifClick?: () => void;
  isGifSelected?: boolean;
  isYoutubeSelected?: boolean;
  isGameModalOpen?: () => void;
  selectedProjectId?: string;
  onTotalCostChange?: (cost: number) => void;
  isGamePost: boolean;
}

export const TokenFeatureSelector = ({
  onFeaturesChange,
  profile,
  editor,
  onGifClick,
  isGifSelected,
  isYoutubeSelected,
  selectedProjectId,
  isGameModalOpen,
  onTotalCostChange,
  isGamePost,
}: Props) => {
  const [selectedFeatures, setSelectedFeatures] = useState<Set<FeatureType>>(
    new Set()
  );

  const level = profile?.level ?? 0;

  // const features: Feature[] = [
  //   { id: 'video', icon: 'post-video', cost: 4, enabled: level >= 3 },
  //   { id: 'image', icon: 'post-image', cost: 2, enabled: level >= 3 },
  //   { id: 'youtube', icon: 'add-youtube', cost: 1, enabled: level >= 2 },
  //   { id: 'gif', icon: 'gif', cost: 1, enabled: level >= 2 },
  //   { id: 'connect', icon: 'post-connect', cost: 2, enabled: level >= 3 },
  // ];

    const features: Feature[] = [
      { id: 'video', icon: 'post-video', cost: 4, enabled: level >= 1 },
      { id: 'image', icon: 'post-image', cost: 2, enabled: level >= 1 },
      { id: 'youtube', icon: 'add-youtube', cost: 1, enabled: level >= 1 },
      { id: 'gif', icon: 'gif', cost: 1, enabled: level >= 1 },
      // { id: 'connect', icon: 'post-connect', cost: 2, enabled: level >= 3 },
    ];

  useEffect(() => {
    setSelectedFeatures((prev) => {
      const next = new Set(prev);

      if (isGifSelected) {
        next.add('gif');
      } else {
        next.delete('gif');
      }
      return next;
    });
  }, [isGifSelected]);

  useEffect(() => {
    setSelectedFeatures((prev) => {
      const next = new Set(prev);

      if (isYoutubeSelected) {
        next.add('youtube');
      } else {
        next.delete('youtube');
      }
      return next;
    });
  }, [isYoutubeSelected]);

  const toggleFeature = (featureId: FeatureType) => {
    const feature = features.find((f) => f.id === featureId);
    if (!feature || !feature.enabled) return;

    if (featureId === 'gif') {
      onGifClick?.();
      return;
    }

    const next = new Set(selectedFeatures);

    if (next.has(featureId)) {
      next.delete(featureId);
    } else {
      const nextCost = totalCost + feature.cost;
      if (nextCost <= (profile?.tokens ?? 0)) {
        next.add(featureId);
      }
    }

    setSelectedFeatures(next);
    onFeaturesChange?.(Array.from(next));
  };

  const totalCost = Array.from(selectedFeatures).reduce((acc, id) => {
    const f = features.find((ff) => ff.id === id);
    return acc + (f?.cost ?? 0);
  }, 0);

  useEffect(() => {
    onTotalCostChange?.(totalCost);
  }, [totalCost, onTotalCostChange]);

  const getCostStyle = (feature: Feature) => {
    if (!feature.enabled) return 'text-[#8A8A8A]';
    return selectedFeatures.has(feature.id) ? 'text-pink-accent' : 'text-black';
  };

  const boldActive = editor?.isActive('bold');
  const italicActive = editor?.isActive('italic');
  const underlineActive = editor?.isActive('underline');

  return (
    <>
      <div className=" md:px-3 flex items-end w-full gap-1">
        <div className="md:flex hidden gap-1 items-center md:mb-1 md:mr-2 text-xs">
          <Icon name="token" className="size-3" />
          <Icon name="tkn" className="text-black h-3 w-7" />:
        </div>
        <div className="flex justify-between w-full">
          <div className="flex gap-5 items-end">
            {features.map((feature) => {
              // const locked = !feature.enabled;
              const available = (profile?.tokens ?? 0) - totalCost;
              const isSelected = selectedFeatures.has(feature.id);
              const cantAfford = !isSelected && available < feature.cost;

              return (
                <div
                  key={feature.id}
                  className="relative group flex flex-col items-center"
                >
                  {
                    // (locked && (
                    //   <div className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-1 rounded-md bg-white border text-[10px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                    //     <span className="font-bold">
                    //       Level{' '}
                    //       {feature.id === 'gif' || feature.id === 'youtube'
                    //         ? 2
                    //         : 3}
                    //     </span>{' '}
                    //     required
                    //   </div>
                    // )) ||
                    cantAfford && (
                      <div className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-1 rounded-md bg-white border text-[10px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 ">
                        <span className="font-bold">Not enough tokens</span>
                      </div>
                    )
                  }

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    // disabled={locked}
                    className="flex flex-col h-fit gap-3 hover:bg-transparent p-0!"
                    onClick={() => toggleFeature(feature.id)}
                  >
                    <Icon
                      name={feature.icon}
                      className={cn(
                        'size-5 transition-colors text-blue-primary',
                        {
                          // 'text-[#8A8A8A]': locked || cantAfford,
                          'text-[#8A8A8A]': cantAfford,

                          'text-pink-accent': selectedFeatures.has(feature.id),
                        }
                      )}
                    />
                    <Icon
                      name={`token-${feature.cost}`}
                      className={`md:size-6 size-5 ${getCostStyle(feature)}`}
                    />
                  </Button>
                </div>
              );
            })}
            <div className="relative group flex md:hidden flex-col items-center justify-between h-full">
              <button type="button" onClick={isGameModalOpen}>
                <Icon
                  name="project"
                  className={cn(
                    'size-5 text-blue-primary hover:text-pink-accent hover:cursor-pointer',
                    {
                      'text-pink-accent': !!selectedProjectId || isGamePost,
                    }
                  )}
                />
              </button>
              <div className='h-7'/>
            </div>
          </div>

          <div className="md:flex hidden items-end gap-4">
            <div className="flex flex-col gap-3 items-end">
              <div className="flex gap-4 items-center">
                <button type="button" onClick={isGameModalOpen}>
                  <Icon
                    name="project"
                    className={cn(
                      'size-5 text-blue-primary hover:text-pink-accent hover:cursor-pointer',
                      {
                        'text-pink-accent': !!selectedProjectId || isGamePost,
                      }
                    )}
                  />
                </button>
                <EmojiPicker
                  onSelect={(emoji) =>
                    editor?.chain().focus().insertContent(emoji).run()
                  }
                >
                  <Icon
                    name="smile"
                    className="size-5 text-blue-primary hover:text-pink-accent hover:cursor-pointer"
                  />
                </EmojiPicker>

                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleBold().run()}
                >
                  <Icon
                    name="bold"
                    className={cn(
                      'size-4 text-blue-primary hover:text-pink-accent hover:cursor-pointer',
                      {
                        'text-pink-accent': boldActive,
                      }
                    )}
                  />
                </button>

                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleItalic().run()}
                >
                  <Icon
                    name="italic"
                    className={cn(
                      'size-4 text-blue-primary hover:text-pink-accent hover:cursor-pointer',
                      {
                        'text-pink-accent': italicActive,
                      }
                    )}
                  />
                </button>

                <button
                  type="button"
                  onClick={() =>
                    editor?.chain().focus().toggleUnderline().run()
                  }
                >
                  <Icon
                    name="underline"
                    className={cn(
                      'size-5 text-blue-primary hover:text-pink-accent hover:cursor-pointer',
                      {
                        'text-pink-accent': underlineActive,
                      }
                    )}
                  />
                </button>
              </div>

              <span
                className={cn('text-black text-sm', {
                  'text-pink-accent': totalCost > 0,
                })}
              >
                Total: {totalCost}
              </span>
            </div>

            <hr className="h-6 border-l border-gray-300" />
          </div>

          {/* <div className="md:hidden flex items-end gap-4">
            <div className="flex flex-col gap-3 items-end">
            </div>
          </div> */}
        </div>
      </div>
    </>
  );
};
